"use client"

import { Container, Box, Title, Card, Table, Group, Button, Text, Tooltip } from "@forestsync/ui"
import { TbArrowRight } from "react-icons/tb"
import Link from "next/link"
import { useTranslations, useLocale } from "next-intl"
import { getCountryName } from "@forestsync/db"
import type { SinkWithStats } from "@/lib/queries"

function timeAgo(date: Date, locale: string): string {
  // European exchange: map generic "en" to en-GB for DD/MM/YYYY ordering
  const displayLocale = locale === "en" ? "en-GB" : locale
  const rtf = new Intl.RelativeTimeFormat(displayLocale, { numeric: "auto" })
  const diffMs = Date.now() - date.getTime()
  const diffSecs = Math.round(diffMs / 1000)
  if (diffSecs < 60) return rtf.format(-diffSecs, "second")
  const diffMins = Math.round(diffSecs / 60)
  if (diffMins < 60) return rtf.format(-diffMins, "minute")
  const diffHours = Math.round(diffMins / 60)
  if (diffHours < 24) return rtf.format(-diffHours, "hour")
  const diffDays = Math.round(diffHours / 24)
  if (diffDays < 7) return rtf.format(-diffDays, "day")
  const diffWeeks = Math.round(diffDays / 7)
  if (diffWeeks < 5) return rtf.format(-diffWeeks, "week")
  const diffMonths = Math.round(diffDays / 30)
  if (diffMonths < 12) return rtf.format(-diffMonths, "month")
  return rtf.format(-Math.round(diffDays / 365), "year")
}

function toUtcLabel(date: Date): string {
  return date.toISOString().replace("T", " ").slice(0, 16) + " UTC"
}

interface LandingMarketActivityProps {
  traded: SinkWithStats[]
}

export function LandingMarketActivity({ traded }: LandingMarketActivityProps) {
  const t = useTranslations("landing")
  const locale = useLocale()

  if (traded.length === 0) return null

  return (
    <Box py={80} px={{ base: "md", lg: 0 }} bg="var(--mantine-color-body)">
      <Container size="lg" maw={800}>
        <Title order={2} ta="center" mb="xl">{t("recentTrades")}</Title>

        <Card withBorder radius="md" p={0} className="overflow-x-auto">
          <Table striped highlightOnHover verticalSpacing="sm" miw={640}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("project")}</Table.Th>
                <Table.Th>{t("vintage")}</Table.Th>
                <Table.Th>{t("volume")} (tCO₂e)</Table.Th>
                <Table.Th>{t("lastPrice")}</Table.Th>
                <Table.Th ta="right">{t("tradedAt")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {traded.slice(0, 5).map((sink, idx) => {
                const isBlurred = idx >= 3
                const href = sink.marketVintage
                  ? `/market/${sink.refId}/${sink.marketVintage}`
                  : `/market`
                return (
                  <Table.Tr key={`trade-${String(sink.id)}`}>
                    <Table.Td>
                      {isBlurred ? (
                        <Text fw={500} size="sm" className="blur-sm select-none">████████████</Text>
                      ) : (
                        <>
                          <Link href={href} className="no-underline text-inherit">
                            <Text fw={500} size="sm" truncate="end" maw={220}>{sink.name}</Text>
                          </Link>
                          <Text size="xs" c="dimmed">{sink.region}, {getCountryName(sink.country)}</Text>
                        </>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500} className={isBlurred ? "blur-sm select-none" : ""}>{isBlurred ? "████" : (sink.marketVintage ?? "N/A")}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} align="center" wrap="nowrap">
                        <div className="w-4 h-4 svg-primary [mask-image:url(/credit-icon.svg)] [-webkit-mask-image:url(/credit-icon.svg)]" />
                        <Text fw={500} size="sm" className={isBlurred ? "blur-sm select-none" : ""}>{isBlurred ? "███" : `${sink.lastTradeVolume?.toLocaleString() ?? "-"}`} <Text component="span" size="xs" c="dimmed">tCO₂e</Text></Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700} className={isBlurred ? "blur-sm select-none" : ""}>
                        {isBlurred ? "███" : `€${sink.lastPrice?.toFixed(2) ?? "-"}`}
                        {!isBlurred && <Text component="span" size="xs" c="dimmed"> / tCO₂e</Text>}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      {isBlurred ? (
                        <Text size="sm" c="dimmed" className="blur-sm select-none">██████</Text>
                      ) : sink.lastTradeAt ? (
                        <Tooltip label={toUtcLabel(new Date(sink.lastTradeAt))} withArrow>
                          <Text size="sm" c="dimmed" className="cursor-default">
                            {timeAgo(new Date(sink.lastTradeAt), locale)}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Card>

        <Group justify="center" mt="lg" gap="sm">
          <Button
            component={Link}
            href="/register"
            variant="filled"
            color="green"
            autoContrast
          >
            {t("signInToSeeMore")}
          </Button>
          <Button
            component={Link}
            href="/market"
            variant="subtle"
            color="green"
            rightSection={<TbArrowRight />}
          >
            {t("exploreAllMarkets")}
          </Button>
        </Group>
      </Container>
    </Box>
  )
}
